import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Typography } from '@/ui';

import { getStytchB2BHeadlessClient } from '@/lib/stytchB2B';
import {
  useAuthenticateDiscoveryMutation,
  useConfirmDiscoveryMutation,
  useHandoffStytchSessionMutation,
  useLazyGetAuthStatusQuery,
  useLoginUserMutation,
} from '@/store/api/authApi';
import { useLazyGetCurrentUserSessionProbeQuery } from '@/store/api/userApi';
import { logAuthDebug } from '@/utils/authDebug';
import {
  clearStoredPostAuthRedirect,
  normalizePostAuthRedirect,
  readPostAuthRedirect,
} from '@/utils/authRedirect';
import { captureException, captureMessage } from '@/utils/errorMonitoring';
import {
  forgetSigninEmail,
  isSameEmail,
  readSigninEmail,
} from '@/utils/signinEmail';
import { identify, identifyEmail, track } from '@/utils/tracking';

const isAuthDebugEnabled = (): boolean => {
  if (!import.meta.env.DEV) {
    return false;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery =
      params.get('debug_auth') === '1' || params.get('debugAuth') === '1';
    const fromStorage = window.localStorage.getItem('DEBUG_AUTH') === 'true';
    return fromQuery || fromStorage;
  } catch {
    return false;
  }
};

const authDebug = (event: string, data?: Record<string, unknown>) => {
  if (!isAuthDebugEnabled()) {
    return;
  }

  // eslint-disable-next-line no-console -- dev-only gated debug logging
  console.info(`[auth-debug] ${event}`, data ?? {});
};

type DiscoveryMember = {
  email_address?: string;
  emailAddress?: string;
  member_id?: string;
  memberId?: string;
  name?: {
    first_name?: string;
    firstName?: string;
    last_name?: string;
    lastName?: string;
  };
};

type CallbackAttemptMetadata = {
  auth_callback_attempt_id: string;
  has_token: boolean;
  redirect_present: boolean;
  stage: string;
  stytch_token_type: string;
  stytch_token_type_normalized: string;
  token_fingerprint: string;
};

type EmailMismatchChoice = 'continue_as_returned' | 'sign_in_as_typed';

type EmailMismatch = {
  choose: (choice: EmailMismatchChoice) => void;
  returnedEmail: string;
  typedEmail: string;
};

type CallbackDedupeStatus =
  'branch_required' | 'failed' | 'in_flight' | 'succeeded';

type CallbackDedupeRecord = {
  attemptId: string;
  errorMessage?: string;
  finalRedirect?: string;
  oauthOnly?: boolean;
  status: CallbackDedupeStatus;
  timestamp: number;
};

const CALLBACK_DEDUPE_PREFIX = 'b2b-auth-callback';
const CALLBACK_DEDUPE_TTL_MS = 30 * 60 * 1000;
const CALLBACK_DEDUPE_IN_FLIGHT_WAIT_MS = 15 * 1000;
const CALLBACK_DEDUPE_POLL_INTERVAL_MS = 250;

const createCallbackAttemptId = (): string => {
  try {
    return window.crypto.randomUUID();
  } catch {
    return `attempt-${Date.now()}`;
  }
};

const fallbackTokenFingerprint = (token: string): string => {
  let hash = 5381;
  for (let index = 0; index < token.length; index += 1) {
    hash = (hash * 33 + token.charCodeAt(index)) % 4_294_967_296;
  }
  return `fallback-${Math.floor(hash).toString(16).padStart(8, '0')}`;
};

const fingerprintToken = (token: string): string => {
  if (!token) {
    return '';
  }

  // Keep fingerprinting synchronous so concurrent callback mounts cannot both
  // yield before the dedupe in-flight claim is written. Tokens are high-entropy
  // single-use values; this short stable fingerprint is only for same-browser
  // replay correlation and never leaves us able to recover the raw token.
  return fallbackTokenFingerprint(token);
};

const buildCallbackMetadata = ({
  attemptId,
  hasToken,
  normalizedTokenType,
  redirect,
  stage,
  tokenFingerprint,
  tokenType,
}: {
  attemptId: string;
  hasToken: boolean;
  normalizedTokenType: string;
  redirect: string | null;
  stage: string;
  tokenFingerprint: string;
  tokenType: string;
}): CallbackAttemptMetadata => ({
  auth_callback_attempt_id: attemptId,
  has_token: hasToken,
  redirect_present: !!redirect,
  stage,
  stytch_token_type: tokenType,
  stytch_token_type_normalized: normalizedTokenType,
  token_fingerprint: tokenFingerprint,
});

const scrubCallbackUrl = ({
  normalizedTokenType,
  redirect,
  tokenFingerprint,
}: {
  normalizedTokenType: string;
  redirect: string | null;
  tokenFingerprint: string;
}): void => {
  try {
    const params = new URLSearchParams();
    params.set('auth_callback', 'processing');
    params.set('callback_token_type', normalizedTokenType);
    params.set('token_fingerprint', tokenFingerprint);
    if (redirect) {
      params.set('redirect', redirect);
    }
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `/auth/discovery-callback${query ? `?${query}` : ''}`,
    );
  } catch {
    // URL scrubbing is best-effort; never block auth on history failures.
  }
};

const getCallbackDedupeKey = (
  normalizedTokenType: string,
  tokenFingerprint: string,
): string =>
  `${CALLBACK_DEDUPE_PREFIX}:${normalizedTokenType}:${tokenFingerprint}`;

const readCallbackDedupeRecord = (key: string): CallbackDedupeRecord | null => {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const record = JSON.parse(raw) as Partial<CallbackDedupeRecord>;
    if (
      !record.attemptId ||
      !record.status ||
      typeof record.timestamp !== 'number'
    ) {
      window.sessionStorage.removeItem(key);
      return null;
    }

    if (Date.now() - record.timestamp > CALLBACK_DEDUPE_TTL_MS) {
      window.sessionStorage.removeItem(key);
      return null;
    }

    return record as CallbackDedupeRecord;
  } catch {
    return null;
  }
};

const writeCallbackDedupeRecord = (
  key: string,
  record: CallbackDedupeRecord,
): void => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(record));
  } catch {
    // Dedupe is best-effort; never block auth if storage is unavailable.
  }
};

const waitForCallbackDedupeResolution = (
  key: string,
): Promise<CallbackDedupeRecord | null> =>
  new Promise((resolve) => {
    let intervalId: number | undefined;
    let timeoutId: number | undefined;
    const finish = (record: CallbackDedupeRecord | null) => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      resolve(record);
    };

    intervalId = window.setInterval(() => {
      const record = readCallbackDedupeRecord(key);
      if (!record || record.status !== 'in_flight') {
        finish(record);
      }
    }, CALLBACK_DEDUPE_POLL_INTERVAL_MS);

    timeoutId = window.setTimeout(() => {
      finish(readCallbackDedupeRecord(key));
    }, CALLBACK_DEDUPE_IN_FLIGHT_WAIT_MS);
  });

const RECOVERABLE_SINGLE_USE_ERROR_TYPES = new Set([
  'magic_link_already_used',
  'magic_link_expired',
  'oauth_token_not_found',
  'token_expired',
  'unable_to_auth_magic_link',
  'unauthorized_credentials',
]);

const getDiscoveryErrorType = (error: unknown): string => {
  const err =
    typeof error === 'object' && error !== null
      ? (error as {
          data?: { error?: string; error_type?: string };
          error?: string;
          error_type?: string;
        })
      : {};

  return (
    err.error_type ?? err.data?.error_type ?? err.data?.error ?? err.error ?? ''
  );
};

const isRecoverableSingleUseError = (error: unknown): boolean =>
  RECOVERABLE_SINGLE_USE_ERROR_TYPES.has(getDiscoveryErrorType(error));

const DiscoveryCallback = () => {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [authenticateDiscovery] = useAuthenticateDiscoveryMutation();
  const [confirmDiscovery] = useConfirmDiscoveryMutation();
  const [handoffStytchSession] = useHandoffStytchSessionMutation();
  const [getAuthStatus] = useLazyGetAuthStatusQuery();
  const [getCurrentUserSessionProbe] = useLazyGetCurrentUserSessionProbeQuery();
  const [loginUser] = useLoginUserMutation();
  const requestInFlight = useRef(false);
  const callbackAttemptId = useRef(createCallbackAttemptId());
  const stytchB2B = useMemo(() => getStytchB2BHeadlessClient(), []);
  const [emailMismatch, setEmailMismatch] = useState<EmailMismatch | null>(
    null,
  );

  // Keep stable refs for RTK mutation triggers so the effect doesn't re-fire
  // if trigger identity changes across renders.
  const authenticateDiscoveryRef = useRef(authenticateDiscovery);
  authenticateDiscoveryRef.current = authenticateDiscovery;
  const confirmDiscoveryRef = useRef(confirmDiscovery);
  confirmDiscoveryRef.current = confirmDiscovery;
  const handoffStytchSessionRef = useRef(handoffStytchSession);
  handoffStytchSessionRef.current = handoffStytchSession;
  const getAuthStatusRef = useRef(getAuthStatus);
  getAuthStatusRef.current = getAuthStatus;
  const getCurrentUserSessionProbeRef = useRef(getCurrentUserSessionProbe);
  getCurrentUserSessionProbeRef.current = getCurrentUserSessionProbe;
  const loginUserRef = useRef(loginUser);
  loginUserRef.current = loginUser;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const token = search.get('token') ?? '';
  const tokenType = search.get('stytch_token_type') ?? '';
  const normalizedTokenType =
    tokenType === 'discovery' ? 'discovery_magic_links' : tokenType;
  const callbackTokenType = search.get('callback_token_type') ?? '';
  const callbackTokenFingerprint = search.get('token_fingerprint') ?? '';
  const isProcessingCallback = search.get('auth_callback') === 'processing';
  const redirect = search.get('redirect') ?? readPostAuthRedirect();
  const finalRedirect = normalizePostAuthRedirect(redirect);

  const formatFrontendDiscoveryError = (error: unknown): string => {
    const stytchError =
      typeof error === 'object' && error !== null
        ? (error as {
            error_message?: string;
            error_type?: string;
            message?: string;
            status?: number;
            status_code?: number;
          })
        : {};

    const status = stytchError.status ?? stytchError.status_code;
    const errorType = stytchError.error_type;

    if (status === 429 || errorType === 'too_many_requests') {
      return 'Too many attempts. Please wait a few minutes and try again.';
    }

    if (
      errorType === 'magic_link_already_used' ||
      errorType === 'magic_link_expired' ||
      errorType === 'token_expired' ||
      errorType === 'unable_to_auth_magic_link' ||
      errorType === 'unauthorized_credentials'
    ) {
      return 'This sign-in link has expired or has already been used. Please request a new one.';
    }

    return 'We couldn’t finish signing you in. Please try again. If this keeps happening, contact concierge@mento.co.';
  };

  const buildCompanyEligibleBranchUrl = (
    tokenValue: string,
    email: string,
  ): string => {
    const params = new URLSearchParams();
    params.set('branch', 'company_eligible');
    params.set('token', tokenValue);
    params.set('email', email);
    if (redirect) {
      params.set('redirect', redirect);
    }

    return `/signin?${params.toString()}`;
  };

  useEffect(() => {
    if (requestInFlight.current) {
      return;
    }

    const navigateToSigninError = (message: string, oauthOnly = false) => {
      const params = new URLSearchParams();
      params.set('error', message);
      if (oauthOnly) {
        params.set('oauth_only', 'true');
        params.set('redirect', finalRedirect);
      }
      navigateRef.current(`/signin?${params.toString()}`, { replace: true });
    };

    const hasExistingSession = async (): Promise<boolean> => {
      try {
        await getAuthStatusRef.current().unwrap();
        return true;
      } catch {
        // Existing auth behavior can still have a valid user session when
        // /auth/status rejects, so fall back to /users/me before failing.
      }

      try {
        const user = (await getCurrentUserSessionProbeRef
          .current()
          .unwrap()) as { id?: string };
        return !!user.id;
      } catch {
        return false;
      }
    };

    const navigateFromDedupeRecord = async (
      key: string,
      record: CallbackDedupeRecord,
    ) => {
      if (record.status === 'branch_required') {
        navigateToSigninError(
          'We need you to continue sign-in from the original tab. If that tab is no longer open, please sign in again.',
        );
        return true;
      }

      if (record.status === 'succeeded') {
        navigateRef.current(record.finalRedirect ?? finalRedirect, {
          replace: true,
        });
        return true;
      }

      if (record.status === 'failed') {
        if (!record.oauthOnly && (await hasExistingSession())) {
          const recoveredRedirect = record.finalRedirect ?? finalRedirect;
          writeCallbackDedupeRecord(key, {
            attemptId: callbackAttemptId.current,
            finalRedirect: recoveredRedirect,
            status: 'succeeded',
            timestamp: Date.now(),
          });
          navigateRef.current(recoveredRedirect, { replace: true });
          return true;
        }

        navigateToSigninError(
          record.errorMessage ??
            'This sign-in link has expired or has already been used. Please request a new one.',
          record.oauthOnly,
        );
        return true;
      }

      return false;
    };

    if (!token || !normalizedTokenType) {
      if (
        !isProcessingCallback ||
        !callbackTokenType ||
        !callbackTokenFingerprint
      ) {
        return;
      }

      requestInFlight.current = true;
      (async () => {
        const dedupeKey = getCallbackDedupeKey(
          callbackTokenType,
          callbackTokenFingerprint,
        );
        const existingDedupeRecord = readCallbackDedupeRecord(dedupeKey);
        if (existingDedupeRecord) {
          const resolvedRecord =
            existingDedupeRecord.status === 'in_flight'
              ? await waitForCallbackDedupeResolution(dedupeKey)
              : existingDedupeRecord;
          if (
            resolvedRecord &&
            (await navigateFromDedupeRecord(dedupeKey, resolvedRecord))
          ) {
            requestInFlight.current = false;
            return;
          }
        }

        if (await hasExistingSession()) {
          writeCallbackDedupeRecord(dedupeKey, {
            attemptId: callbackAttemptId.current,
            finalRedirect,
            status: 'succeeded',
            timestamp: Date.now(),
          });
          navigateRef.current(finalRedirect, { replace: true });
        } else {
          const errorMessage =
            'We couldn’t finish signing you in. Please try again.';
          writeCallbackDedupeRecord(dedupeKey, {
            attemptId: callbackAttemptId.current,
            errorMessage,
            finalRedirect,
            status: 'failed',
            timestamp: Date.now(),
          });
          navigateToSigninError(errorMessage);
        }
        requestInFlight.current = false;
      })();
      return;
    }

    requestInFlight.current = true;

    // Stage tracks the most recent step we attempted. On catch we tag the
    // monitoring event with this so we know exactly which await rejected. Default
    // is 'unknown' so we never lose information if catch fires before any
    // await starts.
    let stage: string = 'unknown';

    (async () => {
      const attemptMetadata = (stageName: string, tokenFingerprint: string) =>
        buildCallbackMetadata({
          attemptId: callbackAttemptId.current,
          hasToken: !!token,
          normalizedTokenType,
          redirect,
          stage: stageName,
          tokenFingerprint,
          tokenType,
        });
      const tokenFingerprint = fingerprintToken(token);
      scrubCallbackUrl({
        normalizedTokenType,
        redirect,
        tokenFingerprint,
      });
      const dedupeKey = getCallbackDedupeKey(
        normalizedTokenType,
        tokenFingerprint,
      );
      const existingDedupeRecord = readCallbackDedupeRecord(dedupeKey);
      track('auth_discovery_callback_viewed', {
        ...attemptMetadata('callback_viewed', tokenFingerprint),
        dedupe_status: existingDedupeRecord?.status ?? 'none',
        redirect,
      });

      if (existingDedupeRecord) {
        stage = `dedupe_${existingDedupeRecord.status}`;
        const duplicateMetadata = {
          ...attemptMetadata(stage, tokenFingerprint),
          dedupe_original_attempt_id: existingDedupeRecord.attemptId,
          dedupe_status: existingDedupeRecord.status,
          redirect,
        };
        track(
          'auth_discovery_callback_duplicate_suppressed',
          duplicateMetadata,
        );
        logAuthDebug('discovery/callback_duplicate_suppressed', {
          context: duplicateMetadata,
        });

        if (existingDedupeRecord.status === 'in_flight') {
          const resolvedRecord =
            await waitForCallbackDedupeResolution(dedupeKey);
          if (
            resolvedRecord &&
            (await navigateFromDedupeRecord(dedupeKey, resolvedRecord))
          ) {
            requestInFlight.current = false;
            return;
          }

          if (await hasExistingSession()) {
            writeCallbackDedupeRecord(dedupeKey, {
              attemptId: callbackAttemptId.current,
              finalRedirect,
              status: 'succeeded',
              timestamp: Date.now(),
            });
            navigateRef.current(finalRedirect, { replace: true });
          } else {
            writeCallbackDedupeRecord(dedupeKey, {
              attemptId: callbackAttemptId.current,
              errorMessage:
                'We couldn’t finish signing you in. Please try again.',
              finalRedirect,
              status: 'failed',
              timestamp: Date.now(),
            });
            navigateToSigninError(
              'We couldn’t finish signing you in. Please try again.',
            );
          }
          requestInFlight.current = false;
          return;
        }

        await navigateFromDedupeRecord(dedupeKey, existingDedupeRecord);
        requestInFlight.current = false;
        return;
      }

      writeCallbackDedupeRecord(dedupeKey, {
        attemptId: callbackAttemptId.current,
        finalRedirect,
        status: 'in_flight',
        timestamp: Date.now(),
      });

      try {
        let member: DiscoveryMember | undefined;
        let emailFromDiscovery = '';

        if (normalizedTokenType === 'discovery_confirm') {
          stage = 'confirm_discovery';
          const response = await confirmDiscoveryRef
            .current({ token })
            .unwrap();
          member = response.member as DiscoveryMember | undefined;
        } else if (
          normalizedTokenType === 'discovery_magic_links' ||
          normalizedTokenType === 'discovery_oauth'
        ) {
          stage =
            normalizedTokenType === 'discovery_magic_links'
              ? 'sdk_magic_links_discovery_authenticate'
              : 'sdk_oauth_discovery_authenticate';
          const discoveryResponse =
            normalizedTokenType === 'discovery_magic_links'
              ? await stytchB2B.magicLinks.discovery.authenticate({
                  discovery_magic_links_token: token,
                })
              : await stytchB2B.oauth.discovery.authenticate({
                  discovery_oauth_token: token,
                });

          const discoveryResponseRecord = discoveryResponse as Record<
            string,
            unknown
          >;
          const intermediateSessionToken =
            typeof discoveryResponseRecord.intermediate_session_token ===
            'string'
              ? discoveryResponseRecord.intermediate_session_token
              : '';
          const sessionJwt =
            typeof discoveryResponseRecord.session_jwt === 'string'
              ? discoveryResponseRecord.session_jwt
              : '';
          const sessionToken =
            typeof discoveryResponseRecord.session_token === 'string'
              ? discoveryResponseRecord.session_token
              : '';
          const memberRecord =
            (discoveryResponseRecord.member as
              Record<string, unknown> | undefined) ?? {};
          const rootEmail =
            typeof discoveryResponseRecord.email_address === 'string'
              ? discoveryResponseRecord.email_address
              : '';
          const memberEmail =
            typeof memberRecord.email_address === 'string'
              ? memberRecord.email_address
              : '';
          emailFromDiscovery = rootEmail || memberEmail;

          logAuthDebug('discovery/stytch_discovery_response', {
            context: {
              ...attemptMetadata(stage, tokenFingerprint),
              hasEmail: !!emailFromDiscovery,
              hasIntermediateSessionToken: !!intermediateSessionToken,
              hasMember: Object.keys(memberRecord).length > 0,
              hasSessionJwt: !!sessionJwt,
              hasSessionToken: !!sessionToken,
              stytchTokenType: tokenType,
              stytchTokenTypeNormalized: normalizedTokenType,
            },
          });

          // The SDK await above already succeeded; from here we're choosing
          // between the IST exchange path, the direct-session handoff path,
          // or rejecting due to missing material. Reset stage so the
          // 'missing_discovery_session_material' throw isn't mis-attributed
          // to the SDK step that actually completed.
          stage = 'extract_discovery_session_material';

          if (
            intermediateSessionToken &&
            normalizedTokenType === 'discovery_oauth'
          ) {
            const typedEmail = readSigninEmail().trim();
            forgetSigninEmail();
            if (
              typedEmail &&
              emailFromDiscovery &&
              !isSameEmail(typedEmail, emailFromDiscovery)
            ) {
              // The OAuth provider returned another address than the one typed
              // on /signin (login_hint only pre-fills). Admitting it would
              // create or open a different Mento account, so ask first. The
              // IST stays in this closure: Stytch tokens are single-use.
              stage = 'email_mismatch_choice';
              writeCallbackDedupeRecord(dedupeKey, {
                attemptId: callbackAttemptId.current,
                finalRedirect,
                status: 'branch_required',
                timestamp: Date.now(),
              });
              track(
                'auth_signin_email_mismatch_shown',
                attemptMetadata(stage, tokenFingerprint),
              );
              const choice = await new Promise<EmailMismatchChoice>(
                (resolve) => {
                  setEmailMismatch({
                    choose: resolve,
                    returnedEmail: emailFromDiscovery,
                    typedEmail,
                  });
                },
              );
              setEmailMismatch(null);
              track('auth_signin_email_mismatch_choice', {
                ...attemptMetadata(stage, tokenFingerprint),
                choice,
              });
              if (choice === 'sign_in_as_typed') {
                const signinPath = redirect
                  ? `/signin?redirect=${encodeURIComponent(finalRedirect)}`
                  : '/signin';
                // Router state, not the query: no personal data in URLs.
                navigateRef.current(signinPath, {
                  replace: true,
                  state: { email: typedEmail },
                });
                return;
              }
              // Back in flight, so a reload during admission can still
              // recover the session instead of reporting a lost tab.
              writeCallbackDedupeRecord(dedupeKey, {
                attemptId: callbackAttemptId.current,
                finalRedirect,
                status: 'in_flight',
                timestamp: Date.now(),
              });
            }
          }

          if (intermediateSessionToken) {
            stage = 'backend_discovery_authenticate';
            const response = await authenticateDiscoveryRef
              .current({
                ...(emailFromDiscovery ? { email: emailFromDiscovery } : {}),
                token: intermediateSessionToken,
                tokenType: 'intermediate_session',
              })
              .unwrap();

            if (
              response.type === 'choose_company_or_individual' &&
              response.token &&
              response.email
            ) {
              const branchUrl = buildCompanyEligibleBranchUrl(
                response.token,
                response.email,
              );
              writeCallbackDedupeRecord(dedupeKey, {
                attemptId: callbackAttemptId.current,
                finalRedirect,
                status: 'branch_required',
                timestamp: Date.now(),
              });
              navigateRef.current(branchUrl, { replace: true });
              return;
            }

            // When the backend returns one of the confirm_individual_* outcomes
            // it expects the frontend to follow up with /auth/discovery/confirm
            // so the user is provisioned (or re-linked) as an individual.
            // Without this branch the flow falls through to `member = response.member`
            // (undefined for these outcomes), throws `missing_discovery_member`,
            // and the user sees the generic "We couldn't finish signing you in" error.
            // Since they already verified their email via the magic link or OAuth
            // there is no UI confirmation needed — auto-confirm.
            if (
              (response.type === 'confirm_individual_new_user' ||
                response.type === 'confirm_individual_existing_user') &&
              response.token
            ) {
              logAuthDebug('discovery/auto_confirm_individual_start', {
                context: {
                  ...attemptMetadata(stage, tokenFingerprint),
                  responseType: response.type,
                  stytchTokenType: tokenType,
                  stytchTokenTypeNormalized: normalizedTokenType,
                },
              });
              stage = 'backend_discovery_confirm';
              const confirmResponse = await confirmDiscoveryRef
                .current({ token: response.token })
                .unwrap();
              member = confirmResponse.member as DiscoveryMember | undefined;
              logAuthDebug('discovery/auto_confirm_individual_success', {
                context: {
                  ...attemptMetadata(stage, tokenFingerprint),
                  hasMember: !!member,
                  stytchTokenType: tokenType,
                  stytchTokenTypeNormalized: normalizedTokenType,
                },
              });
            } else {
              member = response.member as DiscoveryMember | undefined;
            }
            logAuthDebug('discovery/backend_authenticate_success', {
              context: {
                ...attemptMetadata(stage, tokenFingerprint),
                hasMember: !!member,
                responseType: response.type,
                stytchTokenType: tokenType,
                stytchTokenTypeNormalized: normalizedTokenType,
              },
            });
          } else if (sessionJwt || sessionToken) {
            const using = sessionJwt ? 'sessionJwt' : 'sessionToken';
            stage = 'backend_handoff_stytch_session';
            await handoffStytchSessionRef
              .current(sessionJwt ? { sessionJwt } : { sessionToken })
              .unwrap();
            logAuthDebug('discovery/backend_cookie_handoff_success', {
              context: {
                ...attemptMetadata(stage, tokenFingerprint),
                stytchTokenType: tokenType,
                stytchTokenTypeNormalized: normalizedTokenType,
                using,
              },
            });
            member = memberRecord as DiscoveryMember;
          } else {
            throw new Error('missing_discovery_session_material');
          }
        } else if (normalizedTokenType === 'password_session') {
          // The B2B widget already authenticated the password and stored the
          // Stytch session in this browser; hand it to Rails, then read the
          // member from that same SDK session for /login.
          const isSessionJwt = token.split('.').length === 3;
          stage = 'backend_handoff_stytch_session';
          await handoffStytchSessionRef
            .current(
              isSessionJwt ? { sessionJwt: token } : { sessionToken: token },
            )
            .unwrap();
          logAuthDebug('discovery/backend_cookie_handoff_success', {
            context: {
              ...attemptMetadata(stage, tokenFingerprint),
              stytchTokenType: tokenType,
              stytchTokenTypeNormalized: normalizedTokenType,
              using: isSessionJwt ? 'sessionJwt' : 'sessionToken',
            },
          });
          stage = 'sdk_password_session_member';
          const sessionMember = await stytchB2B.self.get();
          member = sessionMember
            ? {
                email_address: sessionMember.email_address,
                member_id: sessionMember.member_id,
              }
            : undefined;
        } else {
          stage = 'backend_discovery_authenticate_direct_token';
          const response = await authenticateDiscoveryRef
            .current({
              token,
              tokenType: normalizedTokenType,
            })
            .unwrap();

          if (
            response.type === 'choose_company_or_individual' &&
            response.token &&
            response.email
          ) {
            const branchUrl = buildCompanyEligibleBranchUrl(
              response.token,
              response.email,
            );
            writeCallbackDedupeRecord(dedupeKey, {
              attemptId: callbackAttemptId.current,
              finalRedirect,
              status: 'branch_required',
              timestamp: Date.now(),
            });
            navigateRef.current(branchUrl, { replace: true });
            return;
          }

          // Same auto-confirm path for non-SDK tokens (see comment above).
          if (
            (response.type === 'confirm_individual_new_user' ||
              response.type === 'confirm_individual_existing_user') &&
            response.token
          ) {
            stage = 'backend_discovery_confirm_direct_token';
            const confirmResponse = await confirmDiscoveryRef
              .current({ token: response.token })
              .unwrap();
            member = confirmResponse.member as DiscoveryMember | undefined;
          } else {
            member = response.member as DiscoveryMember | undefined;
          }
        }

        stage = 'extract_member_identity';
        // Backend responses are converted to camelCase by axios-case-converter,
        // but direct Stytch SDK responses use snake_case. Check camelCase first.
        const memberId = member?.memberId ?? member?.member_id ?? '';
        const email =
          member?.emailAddress ?? member?.email_address ?? emailFromDiscovery;

        if (!memberId || !email) {
          throw new Error('missing_discovery_member');
        }

        // Rails maps /login token types to auth methods. A widget password
        // session is a password sign-in, and so is a raw intermediate session:
        // only the password widget (cross-org discovery or MFA) and the
        // discovery password reset page hand one to this callback.
        const loginTokenType =
          normalizedTokenType === 'password_session' ||
          normalizedTokenType === 'intermediate_session'
            ? 'password_authenticate'
            : normalizedTokenType;

        stage = 'identify_email';
        identifyEmail(email);
        stage = 'backend_login';
        const user = await loginUserRef
          .current({
            emails: [{ email }],
            name: {
              first_name:
                member?.name?.firstName ?? member?.name?.first_name ?? '',
              last_name:
                member?.name?.lastName ?? member?.name?.last_name ?? '',
            },
            providers: [],
            stytch_token_type: loginTokenType,
            user_id: memberId,
          })
          .unwrap();

        stage = 'identify_user';
        identify(user);
        logAuthDebug('discovery/navigate_after_authenticate', {
          context: {
            ...attemptMetadata(stage, tokenFingerprint),
            finalRedirect,
            fromRedirect: redirect,
            stytchTokenType: tokenType,
            stytchTokenTypeNormalized: normalizedTokenType,
            userId: user.id,
          },
        });
        stage = 'track_login_success';
        track('auth_login_success', {
          ...attemptMetadata(stage, tokenFingerprint),
          redirect,
          stytch_token_type: tokenType,
          stytch_token_type_normalized: normalizedTokenType,
          user_id: user.id,
        });
        stage = 'clear_redirect_storage';
        clearStoredPostAuthRedirect();
        stage = 'navigate_to_final_redirect';
        writeCallbackDedupeRecord(dedupeKey, {
          attemptId: callbackAttemptId.current,
          finalRedirect,
          status: 'succeeded',
          timestamp: Date.now(),
        });
        navigateRef.current(finalRedirect, { replace: true });
      } catch (error) {
        authDebug('discovery_callback_failed', {
          ...attemptMetadata(stage, tokenFingerprint),
          error,
          redirect,
          stytchTokenType: tokenType,
          stytchTokenTypeNormalized: normalizedTokenType,
        });

        // Normalize for captureException and keep only safe error summary fields
        // in monitoring context. Raw auth/SDK errors can contain token material.
        const errorForMonitoring =
          error instanceof Error
            ? error
            : new Error(`auth_discovery_callback_failed at stage=${stage}`);
        const errorSummary =
          typeof error === 'object' && error !== null
            ? (error as {
                data?: {
                  error?: string;
                  required_auth_method?: string;
                  requiredAuthMethod?: string;
                  reason_code?: string;
                  reasonCode?: string;
                };
                error_type?: string;
                status?: number;
              })
            : {};

        captureException(errorForMonitoring, {
          // Preserve the existing FRONTEND-MG issue grouping identifier while
          // switching the destination to Datadog.
          fingerprint: ['auth_discovery_callback_failed'],
          tags: {
            auth_discovery_failure_stage: stage,
            auth_discovery_token_type: normalizedTokenType,
          },
          extra: {
            ...attemptMetadata(stage, tokenFingerprint),
            originalErrorCode: errorSummary.data?.error,
            originalErrorMessage: errorForMonitoring.message,
            originalErrorName: errorForMonitoring.name,
            originalErrorReasonCode:
              errorSummary.data?.reason_code ?? errorSummary.data?.reasonCode,
            originalErrorStatus: errorSummary.status,
            originalErrorType: errorSummary.error_type,
            redirect,
            stytchTokenType: tokenType,
            stytchTokenTypeNormalized: normalizedTokenType,
          },
          level: 'error',
        });

        // Keep a message event so alerts can key off a stable Datadog log name.
        captureMessage('auth_discovery_callback_failed', {
          tags: { auth_discovery_failure_stage: stage },
          extra: {
            ...attemptMetadata(stage, tokenFingerprint),
            redirect,
            stytchTokenType: tokenType,
            stytchTokenTypeNormalized: normalizedTokenType,
          },
          level: 'error',
        });

        const signInErrorByCode: Record<string, string> = {
          member_mismatch:
            'Your account membership does not match the expected app onboarding record. Contact support.',
          organization_mapping_required:
            'Your organization has not been onboarded for this application yet. Contact support to finish setup.',
          organization_mismatch:
            'Your authenticated organization does not match the organization configured for this app account. Contact support.',
          organization_selection_required:
            'Your account belongs to multiple organizations. Contact support to complete app onboarding.',
          // Backend signaled the email tied to the discovery session could not be
          // verified server-side (reason_code: restart_discovery_with_verified_email).
          // Send the user back through /signin so a fresh, verified discovery flow
          // can run instead of leaving them on a generic concierge fallback.
          discovery_email_verification_required:
            'For your security, please sign in again so we can verify your email.',
        };
        // A retry cannot fix these: support has to repair the account, so say so instead of inviting another try.
        const signInErrorByReasonCode: Record<string, string> = {
          individual_provisioning_retryable:
            'We couldn’t finish setting up your account, and trying again won’t fix it. Please contact concierge@mento.co and we’ll sort it out.',
        };
        const reasonCode =
          errorSummary.data?.reason_code ?? errorSummary.data?.reasonCode ?? '';
        const oauthOnly =
          errorSummary.status === 403 &&
          (errorSummary.data?.required_auth_method ??
            errorSummary.data?.requiredAuthMethod) === 'oauth';
        const signInError = oauthOnly
          ? 'We couldn’t sign you in with email. Please select “Continue with Google” or “Continue with Microsoft” below, based on the account you use for your work email. If you still can’t sign in, contact concierge@mento.co.'
          : (signInErrorByCode[errorSummary.data?.error ?? ''] ??
            signInErrorByReasonCode[reasonCode] ??
            formatFrontendDiscoveryError(error));

        if (isRecoverableSingleUseError(error)) {
          stage = 'auth_status_after_recoverable_failure';
          if (await hasExistingSession()) {
            writeCallbackDedupeRecord(dedupeKey, {
              attemptId: callbackAttemptId.current,
              finalRedirect,
              status: 'succeeded',
              timestamp: Date.now(),
            });
            clearStoredPostAuthRedirect();
            track('auth_discovery_callback_recovered_existing_session', {
              ...attemptMetadata(stage, tokenFingerprint),
              redirect,
            });
            navigateRef.current(finalRedirect, { replace: true });
            return;
          }

          authDebug('discovery_callback_recovery_session_check_failed', {
            ...attemptMetadata(stage, tokenFingerprint),
            redirect,
          });
        }

        writeCallbackDedupeRecord(dedupeKey, {
          attemptId: callbackAttemptId.current,
          errorMessage: signInError,
          finalRedirect,
          oauthOnly,
          status: 'failed',
          timestamp: Date.now(),
        });
        if (oauthOnly) {
          navigateToSigninError(signInError, true);
        } else {
          navigateRef.current(
            `/signin?error=${encodeURIComponent(signInError)}`,
            { replace: true },
          );
        }
      } finally {
        requestInFlight.current = false;
      }
    })();
    // RTK mutation triggers and navigate are accessed via refs to keep the
    // dependency array stable and prevent effect re-fires.
  }, [
    callbackTokenFingerprint,
    callbackTokenType,
    finalRedirect,
    isProcessingCallback,
    normalizedTokenType,
    redirect,
    stytchB2B,
    token,
    tokenType,
  ]);

  if (emailMismatch) {
    const { choose, returnedEmail, typedEmail } = emailMismatch;
    return (
      <div className="flex h-screen flex-1 items-center justify-center bg-sage-100 px-6">
        <div
          className="w-full max-w-[400px] rounded-md bg-white p-6 shadow-md"
          data-testid="signin-email-mismatch"
        >
          <p className="mb-3 text-lg font-bold">Which account?</p>
          <p className="mb-6 text-sm text-gray-700">
            Your Google or Microsoft account signed you in as{' '}
            <strong>{returnedEmail}</strong>, but you entered{' '}
            <strong>{typedEmail}</strong>.
          </p>
          <div className="flex w-full flex-col gap-3">
            <button
              className="rounded-md bg-[#637A6F] px-4 py-3 text-white"
              type="button"
              onClick={() => choose('sign_in_as_typed')}
            >
              Sign in as {typedEmail}
            </button>
            <button
              className="rounded-md border border-[#637A6F] px-4 py-3 text-[#637A6F]"
              type="button"
              onClick={() => choose('continue_as_returned')}
            >
              Continue as {returnedEmail}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-1 items-center justify-center bg-sage-100 px-6">
      <div className="px-6">
        <img
          alt="logo"
          className="col-start-2 mx-auto"
          height="150"
          src="https://mento-space.nyc3.cdn.digitaloceanspaces.com/logo.svg"
          width="200"
        />
        <Typography className="animate-bounce text-center" variant="subtitle2">
          Loading . . .
        </Typography>
      </div>
    </div>
  );
};

export default DiscoveryCallback;
